import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";

import Button from "components/common/Button";
import Dialog, { DialogContent, DialogActions } from "components/common/Dialog";
import WeeklyShiftSelector from "components/CheckInCheckOut/WeeklyShiftSelector";
import { AlertType } from "components/common/Snackbar";

import { showSnackbar } from "store/actions/Utility";
import { updateEmployeeSchedule } from "services/CheckInCheckOut/CheckInAndBreaks";

import {
  INITIAL_WEEK_SCHEDULE,
  convertAPIScheduleToComponentFormat,
  prepareScheduleDataForAPI,
  isScheduleValid,
} from "../helpers";

import "./EditShiftModal.scss";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";

function EditShiftModal({ open, onClose, onSuccess, employeeId, currentSchedule, assignToCustomer, timezone }) {
  const reduxDispatch = useDispatch();
  const [weekSchedule, setWeekSchedule] = useState(INITIAL_WEEK_SCHEDULE);
  const [sameForAllDays, setSameForAllDays] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      if (currentSchedule && Object.keys(currentSchedule).length > 0) {
        const initialSchedule = convertAPIScheduleToComponentFormat(currentSchedule);
        setWeekSchedule(initialSchedule);
      } else {
        setWeekSchedule(INITIAL_WEEK_SCHEDULE);
      }
    }
  }, [open, currentSchedule]);

  const handleSave = async () => {
    if (!isScheduleValid(weekSchedule)) {
      reduxDispatch(
        showSnackbar({
          type: AlertType.error,
          message: "Please set shift times for at least one working day",
          dispatch: reduxDispatch,
        })
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const scheduleData = prepareScheduleDataForAPI(weekSchedule, timezone);
      await updateEmployeeSchedule(employeeId, scheduleData);

      reduxDispatch(
        showSnackbar({
          type: AlertType.success,
          message: "Shift timings updated successfully",
          dispatch: reduxDispatch,
        })
      );

      if (onSuccess) {
        onSuccess();
      }

      handleClose();
    } catch (error) {
      reduxDispatch(
        showSnackbar({
          type: AlertType.error,
          message: error.message || "Failed to update shift timings",
          dispatch: reduxDispatch,
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setWeekSchedule(INITIAL_WEEK_SCHEDULE);
      setSameForAllDays(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      width="688px"
      title={
        <div className="flex flex-col gap-025">
          {assignToCustomer && (
            <div className="flex items-center gap-025 cursor-pointer" onClick={handleClose}>
              <ChevronLeftIcon className="color-text-heading-secondary" />
              <p className="para-body-m-medium color-text-heading-secondary">Assign to Customer</p>
            </div>
          )}
          <div className="edit-shift-modal__title-section">
            <h2 className="heading-h2-semibold">Edit shift timings</h2>
          </div>
        </div>
      }
      content={
        <DialogContent>
          <div className="edit-shift-modal__content">
            <WeeklyShiftSelector
              weekSchedule={weekSchedule}
              onWeekScheduleChange={setWeekSchedule}
              sameForAllDays={sameForAllDays}
              onSameForAllDaysChange={setSameForAllDays}
              helperText="Please enter all times in your local timezone."
              showHelperText
            />
          </div>
        </DialogContent>
      }
      actions={
        <DialogActions
          action1={<Button label="Cancel" size="large" variant="text" onClick={handleClose} disabled={isSubmitting} />}
          action2={
            <Button
              label="Change Shift Time"
              size="large"
              variant="contained"
              color="primary"
              onClick={handleSave}
              isDisabled={!isScheduleValid(weekSchedule) || isSubmitting}
            />
          }
        />
      }
      size="sm"
    />
  );
}

EditShiftModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  employeeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  currentSchedule: PropTypes.shape({
    sunday: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        startTime: PropTypes.string,
        endTime: PropTypes.string,
      }),
    ]),
    monday: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        startTime: PropTypes.string,
        endTime: PropTypes.string,
      }),
    ]),
    tuesday: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        startTime: PropTypes.string,
        endTime: PropTypes.string,
      }),
    ]),
    wednesday: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        startTime: PropTypes.string,
        endTime: PropTypes.string,
      }),
    ]),
    thursday: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        startTime: PropTypes.string,
        endTime: PropTypes.string,
      }),
    ]),
    friday: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        startTime: PropTypes.string,
        endTime: PropTypes.string,
      }),
    ]),
    saturday: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        startTime: PropTypes.string,
        endTime: PropTypes.string,
      }),
    ]),
  }),
  timezone: PropTypes.string,
};

EditShiftModal.defaultProps = {
  onSuccess: null,
  currentSchedule: {},
  timezone: null,
};

export default EditShiftModal;
